import { IAssetSensorMarker } from '../interfaces'
import { Box, useTheme } from '@mui/material'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import {
    TAB_PAGE_VIEW,
    FMTABS,
    sensorCircleHeight,
    sensorCircleWidth,
    URLSearchParamsKeys,
    Applications,
    UserPermissions,
    floorLayoutDropTargets,
} from '../../../../constants/constants'
import React, { ReactElement, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DragPreviewImage, useDrag } from 'react-dnd'
import { useAppSelector } from '../../../../redux/hook'
import { Tooltip } from 'react-tooltip'
import {
    getAttributeById,
    isDynamic,
    isGPS,
    isHidden,
} from '../../../../utils/AttributeUtils'
import KeyValuePair from '../../../Common/KeyValuePair'
import { isDate1BiggerThanDate2 } from '../../../../utils/DateAndTimeUtils'
import { ILatLong } from '../../Intransit/interfaces'
import { isCriticalAlarm } from '../../../../utils/MiscellaneousUtils'
import { useTranslation } from 'react-i18next'

interface ISensorCircle {
    sensorMarker: IAssetSensorMarker
    isEditMode: boolean
}
const SensorCircle = (props: ISensorCircle): ReactElement => {
    const { sensorMarker, isEditMode } = props
    const { x, y, assetInstanceId, assetName, sensor, alarmStatus } =
        sensorMarker
    const fmSlice = useAppSelector((state) => state.facilityMonitoring)
    const { assetEntityTemplateId, sensorReadingAttributeId } = fmSlice
    const permissions = useAppSelector((state) => state.org.permissions)
    const canDrag = permissions.includes(UserPermissions.ENTITY_WRITER)
    const theme = useTheme()
    const { t } = useTranslation()
    const [searchParams, setSearchParams] = useSearchParams()
    const onClickSensor = () => {
        const nextParams = new URLSearchParams(searchParams)
        nextParams.set(
            URLSearchParamsKeys.tabIndex,
            FMTABS.ASSET_VIEW.toString(),
        )
        nextParams.set(URLSearchParamsKeys.pageView, TAB_PAGE_VIEW.DETAILS_VIEW)
        nextParams.set(
            URLSearchParamsKeys.applicationId,
            Applications.facilityMonitoring.id,
        )
        nextParams.set(URLSearchParamsKeys.instanceId, assetInstanceId)
        nextParams.set(URLSearchParamsKeys.templateId, assetEntityTemplateId)
        setSearchParams(nextParams)
    }
    const reading = getAttributeById(
        sensor?.attributes || [],
        sensorReadingAttributeId,
    )

    const readingValue = reading?.value || ''
    const readingUnit = reading?.unit || ''

    /**
     * If isCurrentAssetReadings is false, then it means that the reading is captured even before the
     * current relation creation. It means it could be reading from previous asset-device(sensor) relation.
     */
    const isCurrentAssetReadings = isDate1BiggerThanDate2(
        sensor?.lastSnapshotUpdated || '',
        sensor?.relationCreationTime || '',
    )

    const sensorReadingAttribute = getAttributeById(
        sensor?.attributes || [],
        sensorReadingAttributeId,
    )
    const displayReading = !isHidden(sensorReadingAttribute)
    const validReading =
        readingValue !== undefined &&
        readingValue !== null &&
        readingValue !== '' &&
        isCurrentAssetReadings

    const getLatLong = useCallback(
        (locationValue: ILatLong) => {
            return (
                <>
                    <KeyValuePair name={t('Lat')} value={locationValue.lat} />
                    <KeyValuePair name={t('Long')} value={locationValue.long} />
                </>
            )
        },
        [t],
    )

    const [, drag, preview] = useDrag({
        type: floorLayoutDropTargets.ASSET_SENSOR_MARKER, // Define the drag type
        item: sensorMarker, // Pass sensor data
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        // end: (dropResult) => {
        //     // Handle drop result (optional)
        // },
        canDrag: () => canDrag,
    })

    const isCritical = isCriticalAlarm(alarmStatus)

    return (
        <Box
            sx={{
                position: 'absolute',
                left: `calc(${x}% - ${sensorCircleWidth / 2}px)`,
                top: `calc(${y}% - ${sensorCircleHeight / 2}px)`,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            {isEditMode && (
                <div
                    ref={(node) => {
                        if (node) drag(node)
                    }}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}
                >
                    <DragPreviewImage
                        connect={preview}
                        src={'/assets/images/map/circle-with-theme-color.svg'}
                    />
                    <DragIndicatorIcon
                        style={{
                            cursor: 'grab',
                            alignSelf: 'center',
                            color: 'rgba(16, 15, 15, 0.87)',
                        }}
                        id={`fm-sensorfloor-drag-indicator-${assetInstanceId}_${sensor?.uid}`}
                    />
                </div>
            )}
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
                data-tooltip-id={`${assetInstanceId}_${sensor?.uid}_my-tooltip-children-multiline`}
            >
                <button
                    onClick={onClickSensor}
                    style={{
                        width: `${sensorCircleWidth}px`,
                        height: `${sensorCircleHeight}px`,
                        borderRadius: '50%',
                        backgroundColor: isCritical
                            ? theme.palette.secondary.dark
                            : theme.palette.primary.main,
                        borderWidth: '0px',
                        cursor: 'pointer',
                        marginRight: '10px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}
                />
            </a>
            <span style={{ marginRight: '4px' }}>{assetName}</span>
            {displayReading && validReading && (
                <span>
                    ({Number(readingValue).toFixed(1)} {readingUnit})
                </span>
            )}
            <Tooltip
                id={`${assetInstanceId}_${sensor?.uid}_my-tooltip-children-multiline`}
                style={{
                    backgroundColor: '#FFFFFF',
                    color: '#000000',
                    padding: '0px',
                    boxShadow: '0px 3px 6px 0px rgba(0,0,0,0.4)',
                    zIndex: 1,
                }}
                opacity={1}
                disableTooltip={() => !isCurrentAssetReadings}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0px 3px 6px 0px rgba(0,0,0,0.4)',
                        padding: '8px',
                    }}
                >
                    {sensor?.attributes
                        ?.filter(
                            (attribute) =>
                                isDynamic(attribute) &&
                                !isGPS(attribute) &&
                                !isHidden(attribute),
                        )
                        ?.map((attribute) => {
                            const { name, value = '', unit = '' } = attribute
                            return (
                                <React.Fragment key={name}>
                                    <KeyValuePair
                                        name={name}
                                        value={
                                            value
                                                ? `${Number(value).toFixed(1)} ${unit}`
                                                : ''
                                        }
                                    />
                                </React.Fragment>
                            )
                        })}
                    {sensor?.attributes
                        ?.filter(
                            (attribute) =>
                                isDynamic(attribute) &&
                                isGPS(attribute) &&
                                !isHidden(attribute),
                        )
                        ?.map((attribute) => {
                            const { name, value = '' } = attribute
                            return (
                                <React.Fragment key={name}>
                                    {value && getLatLong(value)}
                                </React.Fragment>
                            )
                        })}
                </div>
            </Tooltip>
        </Box>
    )
}

export default SensorCircle
